import { useId } from "react";

export type ServiceArtKind = "training" | "build" | "maintenance" | "engineering";

const GOLD = "#d2a254";
const GOLD_DIM = "rgba(210, 162, 84, 0.42)";
const GOLD_FAINT = "rgba(210, 162, 84, 0.16)";

/** Small top-view quadcopter glyph reused across the illustrations. */
const Quad = ({ x, y, s = 1, dim = false }: { x: number; y: number; s?: number; dim?: boolean }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`} stroke={dim ? GOLD_DIM : GOLD} fill="none">
    <rect x="-11" y="-8" width="22" height="16" rx="4" strokeWidth="2" />
    {[
      [-26, -20],
      [26, -20],
      [-26, 20],
      [26, 20],
    ].map(([ax, ay]) => (
      <g key={`${ax}:${ay}`}>
        <line x1={ax * 0.32} y1={ay * 0.32} x2={ax} y2={ay} strokeWidth="2" />
        <circle cx={ax} cy={ay} r="5" strokeWidth="2" />
        <ellipse cx={ax} cy={ay} rx="15" ry="3.4" strokeWidth="1.4" opacity="0.55" />
      </g>
    ))}
  </g>
);

const Gear = ({
  cx,
  cy,
  r,
  teeth,
  strokeWidth = 2,
}: {
  cx: number;
  cy: number;
  r: number;
  teeth: number;
  strokeWidth?: number;
}) => (
  <g stroke={GOLD} fill="none" strokeWidth={strokeWidth}>
    <circle cx={cx} cy={cy} r={r} />
    <circle cx={cx} cy={cy} r={r * 0.42} />
    {Array.from({ length: teeth }).map((_, i) => (
      <rect
        key={i}
        x={cx - r * 0.11}
        y={cy - r - r * 0.2}
        width={r * 0.22}
        height={r * 0.22}
        rx={1.5}
        transform={`rotate(${(i * 360) / teeth} ${cx} ${cy})`}
      />
    ))}
  </g>
);

/**
 * Vector artwork standing in for photography on the service cards — drawn in
 * the brand palette so the cards read as a set alongside the product photos.
 */
export const ServiceArt = ({ kind }: { kind: ServiceArtKind }) => {
  const uid = useId();
  const bg = `${uid}-bg`;
  const grid = `${uid}-grid`;

  return (
    <svg
      viewBox="0 0 400 300"
      preserveAspectRatio="xMidYMid slice"
      role="presentation"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id={bg} x1="0" y1="0" x2="0.7" y2="1">
          <stop offset="0%" stopColor="#132025" />
          <stop offset="100%" stopColor="#060e11" />
        </linearGradient>
        <pattern id={grid} width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M28 0H0V28" fill="none" stroke={GOLD_FAINT} strokeWidth="1" />
        </pattern>
      </defs>

      <rect width="400" height="300" fill={`url(#${bg})`} />
      <rect width="400" height="300" fill={`url(#${grid})`} opacity="0.55" />

      {kind === "training" && (
        <>
          {/* Shield with a protected airframe inside, plus target brackets */}
          <path
            d="M200 58 L266 86 V158 C266 205 234 234 200 250 C166 234 134 205 134 158 V86 Z"
            fill="rgba(210,162,84,0.05)"
            stroke={GOLD}
            strokeWidth="2.4"
            strokeLinejoin="round"
          />
          <Quad x={200} y={148} s={1.05} />
          {[
            [96, 78, 1, 1],
            [304, 78, -1, 1],
            [96, 230, 1, -1],
            [304, 230, -1, -1],
          ].map(([bx, by, sx, sy]) => (
            <path
              key={`${bx}:${by}`}
              d={`M${bx + 22 * sx} ${by} H${bx} V${by + 22 * sy}`}
              fill="none"
              stroke={GOLD_DIM}
              strokeWidth="2.4"
            />
          ))}
        </>
      )}

      {kind === "build" && (
        <>
          {/* Blueprint: airframe with dimension lines and callouts */}
          <Quad x={200} y={140} s={2.1} />
          <g stroke={GOLD_DIM} strokeWidth="1.6" fill="none">
            <line x1="90" y1="232" x2="310" y2="232" />
            <line x1="90" y1="224" x2="90" y2="240" />
            <line x1="310" y1="224" x2="310" y2="240" />
            <line x1="340" y1="94" x2="340" y2="186" />
            <line x1="332" y1="94" x2="348" y2="94" />
            <line x1="332" y1="186" x2="348" y2="186" />
          </g>
          {[
            [145, 98],
            [255, 98],
            [200, 140],
          ].map(([dx, dy]) => (
            <circle key={`${dx}:${dy}`} cx={dx} cy={dy} r="3" fill={GOLD} opacity="0.8" />
          ))}
        </>
      )}

      {kind === "maintenance" && (
        <>
          <Gear cx={150} cy={150} r={54} teeth={10} strokeWidth={2.4} />
          <Gear cx={246} cy={206} r={32} teeth={8} strokeWidth={2} />
          {/* Wrench */}
          <g stroke={GOLD} strokeWidth="2.4" fill="none" strokeLinecap="round">
            <path d="M232 60 a22 22 0 1 0 26 26 L306 134 a11 11 0 0 1-16 16 L232 98 a22 22 0 0 0-0-38 Z" />
          </g>
          <ellipse
            cx={150}
            cy={150}
            rx="86"
            ry="15"
            fill="none"
            stroke={GOLD_DIM}
            strokeWidth="1.4"
            opacity="0.6"
          />
        </>
      )}

      {kind === "engineering" && (
        <>
          {/* Board traces feeding a central module */}
          <g stroke={GOLD_DIM} strokeWidth="1.8" fill="none" strokeLinecap="round">
            <path d="M40 84 H104 V128 H150" />
            <path d="M40 200 H92 V166 H150" />
            <path d="M360 96 H300 V138 H250" />
            <path d="M360 214 H288 V178 H250" />
          </g>
          {[
            [40, 84],
            [40, 200],
            [360, 96],
            [360, 214],
          ].map(([nx, ny]) => (
            <circle key={`${nx}:${ny}`} cx={nx} cy={ny} r="4" fill={GOLD} opacity="0.85" />
          ))}
          <rect
            x="150"
            y="112"
            width="100"
            height="82"
            rx="6"
            fill="rgba(210,162,84,0.06)"
            stroke={GOLD}
            strokeWidth="2.4"
          />
          <rect x="172" y="134" width="56" height="38" rx="3" fill="none" stroke={GOLD_DIM} strokeWidth="1.6" />
          <path
            d="M96 262 h34 l12-26 12 52 12-38 12 24 h96"
            fill="none"
            stroke={GOLD}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.75"
          />
        </>
      )}
    </svg>
  );
};
